import { useEffect, useState, useCallback } from "react";
import client from "./api";
import { num, bookValue } from "./holdingsMath";

export const ALERT_APPS = [
  { v: "family", l: "Family Tree — headcount" },
  { v: "holdings", l: "Holdings — book value" },
  { v: "currency", l: "Currency — base value" },
  { v: "benefits", l: "Benefits — monthly amount" },
];
export const ALERT_LEVELS = [
  { v: "total", l: "Total (whole app)" },
  { v: "item", l: "Single item (by name)" },
  { v: "category", l: "Category / group (by name)" },
];

const tokens = (s) => String(s || "").toLowerCase().split(",").map((x) => x.trim()).filter(Boolean);

export function measureValue(rule, data) {
  const { holdings = [], currencies = [], benefits = [], persons = [] } = data;
  const name = (rule.name || "").trim().toLowerCase();
  const match = (v) => String(v || "").trim().toLowerCase() === name;
  if (rule.app === "holdings") {
    if (rule.level === "total") return holdings.reduce((a, r) => a + bookValue(r), 0);
    if (rule.level === "item") return holdings.filter((r) => match(r.name)).reduce((a, r) => a + bookValue(r), 0);
    if (rule.level === "category") return holdings.filter((r) => match(r.category)).reduce((a, r) => a + bookValue(r), 0);
  }
  if (rule.app === "currency") {
    const val = (r) => num(r.balance) * (num(r.rate) || 1);
    if (rule.level === "total") return currencies.reduce((a, r) => a + val(r), 0);
    if (rule.level === "item") return currencies.filter((r) => match(r.name)).reduce((a, r) => a + val(r), 0);
    if (rule.level === "category") return currencies.filter((r) => match(r.type)).reduce((a, r) => a + val(r), 0);
  }
  if (rule.app === "benefits") {
    if (rule.level === "total") return benefits.reduce((a, r) => a + num(r.amount), 0);
    if (rule.level === "item") return benefits.filter((r) => match(r.name)).reduce((a, r) => a + num(r.amount), 0);
    if (rule.level === "category") return benefits.filter((r) => match(r.type) || tokens(r.groups).includes(name)).reduce((a, r) => a + num(r.amount), 0);
  }
  if (rule.app === "family") {
    if (rule.level === "total") return persons.length;
    if (rule.level === "category") return persons.filter((p) => tokens(p.groups).includes(name)).length;
  }
  return 0;
}

export function isBreached(rule, value) {
  const limit = num(rule.limit);
  return rule.direction === "below" ? value < limit : value > limit;
}

export async function loadAlertData() {
  const [a, h, c, b, p] = await Promise.all([
    client.get("/alerts"), client.get("/holdings"), client.get("/currencies"), client.get("/benefits"), client.get("/persons"),
  ]);
  const map = (x) => x.data.map((d) => ({ id: d.id, ...(d.data || d) }));
  return { rules: map(a), holdings: map(h), currencies: map(c), benefits: map(b), persons: map(p) };
}

export function evaluate(rules, data) {
  return rules.filter((r) => r.enabled !== false).map((r) => {
    const value = measureValue(r, data);
    return { rule: r, value, breached: isBreached(r, value) };
  });
}

export function useBreaches() {
  const [state, setState] = useState({ results: [], breaches: [], data: null });
  const refresh = useCallback(async () => {
    try {
      const data = await loadAlertData();
      const results = evaluate(data.rules, data);
      setState({ results, breaches: results.filter((r) => r.breached), data });
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { ...state, refresh };
}
